/**
 * Client side of the key broker (B4).
 *
 * `ensureApiKey()` makes the add-in "just work" for a signed-in M365 user: if
 * no key is stored, it silently gets an Entra ID token (NAA) and exchanges it
 * at `/api/broker/key` for that user's LiteLLM key, then stores it like a
 * manually-entered key. Everything is best-effort — any failure leaves the
 * manual key field as the fallback and never blocks the UI.
 */
import { getAiSettings, setAiSettings } from '../ai/aiSettings';
import { buildBrokerUrl } from '../ai/endpoint';
import { acquireIdentity, isNaaSupported } from './naaClient';
import { isAuthConfigured } from './authConfig';

/** How the current key was obtained — drives the Settings UI (B5). */
export type KeySource = 'manual' | 'broker' | 'none';

const SOURCE_KEY = 'v14_ai_keySource';

/** Fired on window whenever the key/source changes, so mounted UI (Settings)
 * can refresh — the startup provisioning is fire-and-forget and may finish
 * AFTER components rendered their initial state. */
export const KEY_CHANGED_EVENT = 'v14-keysource-changed';

export function getKeySource(): KeySource {
  return (localStorage.getItem(SOURCE_KEY) as KeySource) ?? (getAiSettings().apiKey ? 'manual' : 'none');
}

function setKeySource(s: KeySource): void {
  localStorage.setItem(SOURCE_KEY, s);
  try {
    window.dispatchEvent(new CustomEvent(KEY_CHANGED_EVENT));
  } catch {
    /* non-browser env (tests) */
  }
}

/** True when the zero-touch flow is even possible in this session. */
export function brokerAvailable(): boolean {
  return isAuthConfigured() && isNaaSupported();
}

/**
 * Ensures an API key is present, provisioning one via the broker when needed.
 * Returns the resolved source. Safe to call on every startup: it no-ops when a
 * key already exists (manual OR previously brokered) unless `force` is set.
 */
export async function ensureApiKey(opts: { force?: boolean } = {}): Promise<KeySource> {
  const existing = getAiSettings().apiKey;
  if (existing && !opts.force) return getKeySource();

  // A manually-entered key is authoritative — don't override it silently.
  if (existing && getKeySource() === 'manual' && !opts.force) return 'manual';

  if (!brokerAvailable()) return getKeySource();

  const identity = await acquireIdentity();
  if (!identity) return getKeySource();

  try {
    const res = await fetch(buildBrokerUrl('/key'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${identity.idToken}` },
    });
    if (!res.ok) {
      console.warn('[keyBroker] broker returned', res.status);
      return getKeySource();
    }
    const { key } = (await res.json()) as { key?: string };
    if (!key) return getKeySource();
    setAiSettings({ apiKey: key });
    setKeySource('broker');
    return 'broker';
  } catch (err) {
    console.warn('[keyBroker] exchange failed:', err);
    return getKeySource();
  }
}

/** Marks a key as manually entered (call from the Settings key field). */
export function markManualKey(): void {
  setKeySource('manual');
}

/** Clears a brokered key (e.g. sign-out); manual keys are left untouched. */
export function clearBrokeredKey(): void {
  if (getKeySource() === 'broker') {
    setAiSettings({ apiKey: '' });
    setKeySource('none');
  }
}

let recovering: Promise<string | null> | null = null;

/**
 * 401 auto-recovery: when a BROKERED key is rejected (rotated/revoked
 * server-side), silently re-provision via the broker and return the fresh key
 * so the caller can retry once. Returns null for manual keys (the user chose
 * it — surface the error instead) or when re-provisioning fails. Concurrent
 * callers share one recovery round-trip.
 */
export async function recoverBrokeredKey(): Promise<string | null> {
  // Recover whenever the org broker is available — including for a MANUAL/legacy
  // key. A pasted or previously-brokered key that has expired or been rotated
  // should self-heal (the broker can always mint a valid one), instead of
  // dead-ending in an endless 401 loop with no recovery (the failure that
  // stranded early testers on a stale key).
  if (!brokerAvailable()) return null;
  if (!recovering) {
    recovering = (async () => {
      const before = getAiSettings().apiKey;
      const src = await ensureApiKey({ force: true });
      const key = getAiSettings().apiKey;
      // Success ONLY if we obtained a genuinely DIFFERENT brokered key. If
      // sign-in failed and the old (dead) key was kept, report failure so the
      // caller surfaces a clear error rather than retrying the same key.
      return src === 'broker' && key && key !== before ? key : null;
    })().finally(() => {
      recovering = null;
    });
  }
  return recovering;
}
